import React, { useEffect, useState } from "react";
import {
  requestListRewardsRedemption,
  useAuthState,
  usePointDispatch,
  usePointState,
} from "../../Context";
import { useTranslation } from "react-i18next";
import TableSkeleton from "../../Components/TableSkeleton";
import dayjs from "dayjs";
import Badge from "../../Components/Badge";
import { formatIndonesianPhone } from "../../Utils";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import { Pagination } from "../../Components/Pagination";

const RewardRedemptionHistory = () => {
  const { t, i18n } = useTranslation();
  const pointDispatch = usePointDispatch();
  const { redemptions, redemptionsLoading, redemptionsError, nextTokens } =
    usePointState();
  const { user } = useAuthState();

  useEffect(() => {
    if (user?.id) {
      requestListRewardsRedemption(pointDispatch, { owner: user.id, page: 1 });
    }
  }, [user?.id, pointDispatch]);

  const [page, setpage] = useState(1);

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestListRewardsRedemption(pointDispatch, {
        page: nextPage,
        owner: user.id,
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListRewardsRedemption(pointDispatch, {
        page: prevPage,
        owner: user.id,
        nextToken: nextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListRewardsRedemption(pointDispatch, {
        page: selectedPage,
        owner: user.id,
        nextToken: nextTokens[selectedPage],
      });
    }
  };

  const getStatusType = (status) => {
    switch (status) {
      case "PENDING":
        return "alert";
      case "CONFIRMED":
      case "PACKED":
      case "SHIPPED":
      case "IN_TRANSIT":
        return "info";
      case "DELIVERED":
      case "COMPLETED":
      case "ISSUED":
        return "success";
      case "FAILED":
        return "danger";
      default:
        return "secondary";
    }
  };

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
          <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    {t("ID")}
                  </th>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    {t("Date")}
                  </th>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    {t("Product")}
                  </th>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    {t("Description")}
                  </th>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    {t("Status")}
                  </th>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    {t("point")}
                  </th>
                </tr>
              </thead>
              {redemptionsLoading ? (
                <TableSkeleton rows={3} columns={6} />
              ) : (
                <tbody className="divide-y divide-gray-200 bg-white">
                  {redemptions.length > 0 ? (
                    redemptions.map((redemption, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm font-medium text-left text-gray-700 uppercase whitespace-nowrap">
                          <a href={`/rewards-redemption/${redemption.id}`}>
                            {redemption.id?.slice(0, 5)}
                          </a>
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap min-w-32">
                          {dayjs(redemption?.createdAt)
                            .locale(i18n.language)
                            .format("DD MMM YYYY")}
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                          {redemption.rewardProduct?.name}
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700 min-w-96">
                          {redemption.detail?.address?.street ? (
                            redemption.detail.address.street
                          ) : redemption.rewardProduct?.type === "VOUCHER" ? (
                            redemption.status === "ISSUED" ? (
                              <span>
                                {t("voucherCode")} :{" "}
                                {redemption.voucher?.voucher?.id}
                              </span>
                            ) : (
                              <span>{redemption.detail?.email}</span>
                            )
                          ) : (
                            <span>
                              {formatIndonesianPhone(redemption?.detail?.phone)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap min-w-32">
                          <Badge
                            title={redemption?.status?.toLowerCase() || "-"}
                            type={getStatusType(redemption?.status)}
                          />
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap min-w-32">
                          {redemption.rewardProduct?.point?.toLocaleString(
                            "id-ID"
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="p-4" colSpan={12}>
                        {redemptionsError ? (
                          <ErrorList
                            message={
                              redemptionsError?.message ||
                              "Error fetching redemptions."
                            }
                            errors={redemptionsError?.errors}
                          />
                        ) : (
                          <EmptyList title="noRedemptionHistory" />
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </table>
            <Pagination
              page={page}
              onPrev={onPrev}
              onNext={onNext}
              onPageChange={onPageChange}
              nextTokens={nextTokens}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardRedemptionHistory;
